import type { FC } from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { useAction } from '@wix/da-hooks/pkg/useAction';
import Flex from '@wix/da-ds/pkg/Flex';
import { dateToDistanceTrans } from '@wix/da-i18next/pkg/time/functions';
import ExclusiveButton from '@wix/da-ds/pkg/Buttons/ExclusiveButton';
import { pushAdoptsSellerModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import {
  getIsAdopted,
  getIsBeforeOfferEndTime,
  getOfferCountdownEndTime,
} from '@wix/da-shared-react/pkg/utils/adoptables';
import StatItem from '../../../../_partials/StatItem';

import s from './ExclusiveOffersStat.scss';

interface Props {
  deviation: PapiDeviation;
}

const ExclusiveOffersStat: FC<Props> = ({ deviation }) => {
  const { t } = useTranslation();
  const isSold = getIsAdopted(deviation);
  const isDisabled = isSold || !deviation.adoptable?.offers;

  const dispatchPushAdoptsSellerModal = useAction(pushAdoptsSellerModal);
  const onViewOffersClick = useCallback(
    () => dispatchPushAdoptsSellerModal([deviation], 'offers'),
    [dispatchPushAdoptsSellerModal, deviation]
  );

  if (isDisabled) {
    return (
      <StatItem title={t('studio.products.exclusive.stats.offers')} disabled />
    );
  }

  const numOffers = deviation.adoptable?.offers?.numOffers ?? 0;
  const hasOfferTimeLeft = getIsBeforeOfferEndTime(deviation);
  const offerEndTime = getOfferCountdownEndTime(deviation);
  const offerTimeDistance =
    hasOfferTimeLeft &&
    offerEndTime &&
    dateToDistanceTrans(offerEndTime, false, true);

  const viewOffersBtn = numOffers > 0 && (
    <ExclusiveButton
      priority="primary"
      variant="table"
      onClick={onViewOffersClick}
    >
      {t(
        hasOfferTimeLeft
          ? 'studio.products.exclusive.controls.viewOffers'
          : 'studio.products.exclusive.controls.acceptOffers'
      )}
    </ExclusiveButton>
  );

  return (
    <StatItem
      title={
        <Flex gap={4} alignItems="center">
          {t('studio.products.exclusive.stats.offers')}
          {numOffers > 0 && (
            <>
              <div className={s['separator']} />
              {offerTimeDistance ? (
                t('studio.products.exclusive.stats.offers.timeLeft', {
                  timeDistance: t(offerTimeDistance.key, {
                    count: offerTimeDistance.value,
                  }),
                })
              ) : (
                <span className={s['timeup']}>
                  {t('studio.products.exclusive.stats.offers.timeUp')}
                </span>
              )}
            </>
          )}
        </Flex>
      }
      value={
        <Flex gap={8} alignItems="center">
          {numOffers}
          {viewOffersBtn}
        </Flex>
      }
    />
  );
};
ExclusiveOffersStat.displayName = 'ExclusiveOffersStat';

export default ExclusiveOffersStat;
